/**
 * The find & replace panel (`061-editor.md`; `110-design-system.md` §7).
 *
 * **A plain floating element, deliberately not a Radix `Dialog`/`Popover`.** A `role="dialog"`
 * would make `isTypingOrOverlaid` (`hooks/useHotkeys.ts`) treat the whole app as overlaid and
 * suppress every non-`mod` hotkey while the panel is open; and the panel must sit alongside a
 * live editor, not over it, so next/prev can move the caret in the document underneath.
 *
 * Match state (count and the active index) is read from the search plugin via `useEditorState`;
 * the query and replacement strings are ordinary controlled inputs whose changes drive the
 * editor commands. Replace-all is handed back to the host (`onReplacedAll`) so it can persist
 * immediately, the way "gather sub-pages" does.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditorState, type Editor as TiptapEditor } from '@tiptap/react';
import {
  CaseSensitive,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Replace,
  Search,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { scrollCurrentMatchIntoView, searchPluginKey } from '@/features/editor/search';

interface SearchPanelProps {
  editor: TiptapEditor;
  /** Replace is unavailable on an archived page or while a conflict banner is open. */
  editable: boolean;
  /** Bumped each time the open shortcut fires — re-seeds and re-focuses the find field. */
  nonce: number;
  /** The current selection's text, used to seed the find field on (re)open. */
  seed: string;
  /** Whether to reveal the replace row on this open. */
  replaceMode: boolean;
  onClose: () => void;
  /** Fired after a replace-all so the host can flush the save immediately. */
  onReplacedAll: () => void;
}

function IconButton({
  label,
  disabled = false,
  active = false,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex size-6 items-center justify-center rounded-sm text-[var(--muted-foreground)]',
        'hover:bg-[var(--accent)] hover:text-[var(--foreground)]',
        'disabled:pointer-events-none disabled:opacity-40',
        active && 'bg-[var(--accent)] text-[var(--foreground)]',
        '[&_svg]:size-3.5',
      )}
    >
      {children}
    </button>
  );
}

export function SearchPanel({
  editor,
  editable,
  nonce,
  seed,
  replaceMode,
  onClose,
  onReplacedAll,
}: SearchPanelProps) {
  const { t } = useTranslation();
  const [find, setFind] = useState('');
  const [replace, setReplace] = useState('');
  const [showReplace, setShowReplace] = useState(replaceMode);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const findRef = useRef<HTMLInputElement>(null);

  const { count, current } = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      const search = searchPluginKey.getState(e.state);
      return { count: search?.matches.length ?? 0, current: search?.current ?? -1 };
    },
  }) ?? { count: 0, current: -1 };

  // Each open (Mod+F / Mod+Shift+H) re-seeds from the selection, re-runs the query, reveals the
  // replace row if asked, and puts the caret in the find field with its text selected.
  useEffect(() => {
    setFind(seed);
    setShowReplace(replaceMode);
    editor.commands.setSearchTerm(seed);
    scrollCurrentMatchIntoView(editor);
    const input = findRef.current;
    if (input !== null) {
      input.focus();
      input.select();
    }
    // Only when a new open is signalled, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce]);

  // Leaving the panel drops the highlights; the doc keeps whatever edits were made.
  useEffect(() => () => void editor.commands.clearSearch(), [editor]);

  const close = useCallback(() => {
    editor.commands.focus();
    onClose();
  }, [editor, onClose]);

  // Every command that can move the active match is followed by an explicit scroll — see
  // `scrollCurrentMatchIntoView`, which explains why the transaction's own `scrollIntoView()`
  // cannot reach the match while focus stays in the find field.
  const onFindChange = (value: string) => {
    setFind(value);
    editor.commands.setSearchTerm(value);
    scrollCurrentMatchIntoView(editor);
  };

  const toggleCase = () => {
    const next = !caseSensitive;
    setCaseSensitive(next);
    editor.commands.setSearchCaseSensitive(next);
    scrollCurrentMatchIntoView(editor);
  };

  const goNext = () => {
    editor.commands.findNext();
    scrollCurrentMatchIntoView(editor);
  };

  const goPrevious = () => {
    editor.commands.findPrevious();
    scrollCurrentMatchIntoView(editor);
  };

  const replaceOne = () => {
    if (!editable) return;
    // The rematch on `docChanged` moves `current` onto the following match.
    editor.commands.replaceCurrent(replace);
    scrollCurrentMatchIntoView(editor);
  };

  const replaceEvery = () => {
    if (!editable) return;
    if (editor.commands.replaceAll(replace)) onReplacedAll();
  };

  const counter =
    find === ''
      ? ''
      : count === 0
        ? t('editor.search.noResults')
        : t('editor.search.matchCount', { current: current + 1, total: count });

  return (
    <div
      role="search"
      aria-label={t('editor.search.label')}
      className={cn(
        'absolute right-3 top-11 z-20 flex items-start gap-1',
        'rounded-lg border border-[var(--border)] bg-[var(--popover)] p-1.5',
      )}
    >
      <IconButton
        label={showReplace ? t('editor.search.hideReplace') : t('editor.search.showReplace')}
        active={showReplace}
        onClick={() => setShowReplace((value) => !value)}
      >
        {showReplace ? <ChevronDown /> : <ChevronRight />}
      </IconButton>

      <div className="flex flex-col gap-1">
        {/* Find row */}
        <div className="flex items-center gap-1">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-[var(--muted-foreground)]" />
            <Input
              ref={findRef}
              value={find}
              onChange={(event) => onFindChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  if (event.shiftKey) goPrevious();
                  else goNext();
                } else if (event.key === 'Escape') {
                  event.preventDefault();
                  close();
                }
              }}
              placeholder={t('editor.search.findPlaceholder')}
              aria-label={t('editor.search.find')}
              className="h-7 w-56 pl-7"
            />
          </div>

          <span className="w-16 shrink-0 text-center text-xs tabular-nums text-[var(--muted-foreground)]">
            {counter}
          </span>

          <IconButton
            label={t('editor.search.previous')}
            disabled={count === 0}
            onClick={goPrevious}
          >
            <ChevronUp />
          </IconButton>
          <IconButton label={t('editor.search.next')} disabled={count === 0} onClick={goNext}>
            <ChevronDown />
          </IconButton>
          <IconButton
            label={t('editor.search.caseSensitive')}
            active={caseSensitive}
            onClick={toggleCase}
          >
            <CaseSensitive />
          </IconButton>
          <IconButton label={t('editor.search.close')} onClick={close}>
            <X />
          </IconButton>
        </div>

        {/* Replace row */}
        {showReplace && (
          <div className="flex items-center gap-1">
            <div className="relative">
              <Replace className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-[var(--muted-foreground)]" />
              <Input
                value={replace}
                onChange={(event) => setReplace(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    replaceOne();
                  } else if (event.key === 'Escape') {
                    event.preventDefault();
                    close();
                  }
                }}
                placeholder={t('editor.search.replacePlaceholder')}
                aria-label={t('editor.search.replace')}
                disabled={!editable}
                className="h-7 w-56 pl-7"
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!editable || count === 0}
              onClick={replaceOne}
            >
              {t('editor.search.replaceOne')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!editable || count === 0}
              onClick={replaceEvery}
            >
              {t('editor.search.replaceAll')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
